import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/page-header";
import { DateRangeFilter } from "@/components/date-range-filter";
import { BarList, Panel, StatTile } from "@/components/analytics-ui";
import { SkAnalyticsPage } from "@/components/skeleton";
import { PageQuery } from "@/components/page-query";
import type { MetaPage } from "@shared/pages/meta";
import { pageQuery } from "@/lib/queries";
import { useTitle } from "@/lib/title";

// Meta -> Campaigns: where each campaign's budget goes and what
// it brings back. Campaign detail rides the agency's per-client visibility
// toggle; until it's enabled (or the hub is connected) a plain panel renders.

export const Route = createFileRoute("/_app/meta/campaigns")({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(pageQuery<MetaPage>("meta-campaigns", deps));
  },
  component: MetaCampaignsRoute,
});

const usd = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: n >= 100 ? 0 : 2 });
const num = (n: number) => n.toLocaleString("en-US");
const pct = (n: number) => `${(n * 100).toFixed(2)}%`;

const th = "py-1.5 pr-3 font-medium";
const td = "py-2 pr-3 tabular-nums";

function MetaCampaignsRoute() {
  useTitle("Meta campaigns");
  const search = Route.useSearch();
  return (
    <PageQuery<MetaPage> name="meta-campaigns" search={search} skeleton={<SkAnalyticsPage />}>
      {(page) => <MetaCampaigns page={page} />}
    </PageQuery>
  );
}

function MetaCampaigns({ page }: { page: MetaPage }) {
  const { configured, report: live, presets, rangeLabel } = page;

  // No sample data, ever: absent feed or a hidden section renders a plain
  // explanation panel (the sidebar hides this page in that case anyway).
  if (!configured || !live || !live.ads?.campaigns) {
    return (
      <div className="page-shell">
        <PageHeader title="Meta campaigns" />
        <Panel title={!configured || !live ? "Not connected" : "Not enabled"} className="mt-5">
          <p className="text-sm text-muted">
            {!configured || !live
              ? "Meta numbers show here once Digital King connects the Meta account."
              : "Campaign detail is enabled per account by the agency. It will appear here automatically once it's switched on in the agency hub."}
          </p>
        </Panel>
      </div>
    );
  }
  const campaigns = live.ads.campaigns;

  const active = campaigns.filter((c) => c.status === "active");
  const spend = campaigns.reduce((s, c) => s + (c.spend ?? 0), 0);
  const conversions = campaigns.reduce((s, c) => s + (c.conversions ?? 0), 0);
  const showSpend = campaigns.some((c) => c.spend !== undefined);
  const showConv = campaigns.some((c) => c.conversions !== undefined);

  return (
    <div className="page-shell">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader title="Meta campaigns" />
        <DateRangeFilter presets={presets} defaultKey="30d" />
      </div>

      <div className="grid grid-cols-2 gap-3 @3xl:grid-cols-4">
        <StatTile label="Active campaigns" value={String(active.length)} sub={`${campaigns.length - active.length} paused / ended`} />
        {showSpend && <StatTile label="Spend" value={usd(spend)} sub={rangeLabel} />}
        {showConv && <StatTile label="Conversions" value={num(Math.round(conversions))} sub="from Meta ads" />}
        {showSpend && showConv && (
          <StatTile label="Avg cost / conv." value={conversions > 0 ? usd(spend / conversions) : "–"} sub="across campaigns" />
        )}
      </div>

      <Panel title="Campaign performance" className="mt-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted">
                <th className={th}>Campaign</th>
                {showSpend && <th className={`${th} text-right`}>Spend</th>}
                <th className={`${th} text-right`}>Impr.</th>
                <th className={`${th} text-right`}>Clicks</th>
                <th className={`${th} text-right`}>CTR</th>
                {showConv && <th className={`${th} text-right`}>Conversions</th>}
                {showSpend && showConv && <th className={`${th} text-right`}>Cost / conv.</th>}
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.name} className="border-t border-border">
                  <td className="py-2 pr-3 text-foreground">
                    {c.name}
                    {c.status !== "active" && <span className="text-muted"> ({c.status})</span>}
                  </td>
                  {showSpend && <td className={`${td} text-right`}>{c.spend !== undefined ? usd(c.spend) : "-"}</td>}
                  <td className={`${td} text-right`}>{num(c.impressions)}</td>
                  <td className={`${td} text-right`}>{num(c.clicks)}</td>
                  <td className={`${td} text-right`}>{c.impressions > 0 ? pct(c.clicks / c.impressions) : "-"}</td>
                  {showConv && (
                    <td className={`${td} text-right`}>{c.conversions !== undefined ? num(Math.round(c.conversions)) : "-"}</td>
                  )}
                  {showSpend && showConv && (
                    <td className={`${td} text-right`}>
                      {(c.conversions ?? 0) > 0 ? usd((c.spend ?? 0) / c.conversions!) : "-"}
                    </td>
                  )}
                </tr>
              ))}
              {campaigns.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-muted">No campaigns in this range.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      {showSpend && campaigns.length > 0 && (
        <div className="mt-4 grid gap-4 @3xl:grid-cols-2">
          <Panel title="Spend share">
            <BarList
              toneClass="bg-primary"
              items={campaigns
                .filter((c) => (c.spend ?? 0) > 0)
                .map((c) => ({ label: c.name, count: Math.round(c.spend ?? 0), sub: `${num(c.clicks)} clicks` }))}
            />
          </Panel>
          {showConv && (
            <Panel title="Conversions by campaign">
              <BarList
                items={campaigns
                  .filter((c) => (c.conversions ?? 0) > 0)
                  .sort((a, b) => (b.conversions ?? 0) - (a.conversions ?? 0))
                  .map((c) => ({
                    label: c.name,
                    count: Math.round(c.conversions ?? 0),
                    sub: (c.conversions ?? 0) > 0 && c.spend !== undefined ? `${usd((c.spend ?? 0) / c.conversions!)} each` : undefined,
                  }))}
              />
            </Panel>
          )}
        </div>
      )}
    </div>
  );
}
